import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { object, string, boolean, number, array } from 'yup'
import { isNil } from 'lodash'
import { type Sweepstake } from 'types/api'
import { yupResolver } from '@hookform/resolvers/yup'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { useLayout } from 'hooks/useLayout'
import {
  Box,
  Button,
  InputLabel,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
} from '@mui/material'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage/FormFieldErrorMessage'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { usePutLocationSweepstake } from 'hooks/api/usePutLocationSweepstake'
import { SectionHeader } from 'components/SectionHeader'
import { ActivityButton } from 'components/ActivityButton'
import { isPresent } from '@jjvgaming/player-payback-library'
import { useGetSweepstakeById } from 'hooks/api/useGetSweepstakeById'

export const LocationSweepstakeSingleDrawingSection = ({
  sweepstake,
}: {
  sweepstake: Sweepstake
}) => {
  const { isMobile } = useLayout()
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [drawingIdToEdit, setDrawingIdToEdit] = useState<number | null>(null)
  const [drawingNameToEdit, setDrawingNameToEdit] = useState<string | null>(
    null
  )

  const prizesQuery = useGetSweepstakeById(sweepstake.id)

  const prizes = prizesQuery.data

  const putLocationSweepstake = usePutLocationSweepstake({
    ...sweepstake,
    id: drawingIdToEdit ?? 0,
    onSuccess: () => {
      setMessage('Sweepstake has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      toggleModalIsOpen()
    },
    onError: () => {
      setMessage('Error updating Sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const SweepstakeDetailsSchema = object().shape({
    Sweepstake: object({
      ContestWinner: array().of(
        object({
          winnerNumber: number().required(),
          cashPrize: boolean().nullable(),
          prizeType: string(),
          prizeAmount: number()
            .typeError('Prize Amount must be a number 0 or greater')
            .when('cashPrize', {
              is: true,
              then: (schema) =>
                schema
                  .min(0, 'Prize Amount must be 0 or greater')
                  .max(100000, 'Prize Amount cannot exceed 100,000'),
              otherwise: (schema) => schema.optional(),
            }),
          prizeDescription: string().required(
            'Prize Description is a required field'
          ),
        })
      ),
    }),
  })

  const numLocationWinners = prizes?.numLocationWinners ?? 1
  const formMethods = useForm({
    resolver: yupResolver(SweepstakeDetailsSchema),
    mode: 'all',
    defaultValues: {
      Sweepstake: {
        ContestWinner: Array.from({ length: numLocationWinners }, (_, i) => {
          const winner = prizes?.contestWinners?.filter(
            (x: { contestId: any }) => x.contestId === prizes?.id
          )[i]
          return {
            winnerNumber: winner?.winnerNumber ?? i + 1,
            cashPrize: winner?.cashPrize ?? false,
            prizeAmount: Number(winner?.prizeAmount ?? 0),
            prizeDescription: winner?.prizeDescription ?? '',
          }
        }),
      },
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = formMethods

  if (prizesQuery.isPending) {
    return <ActivityIndicator />
  }

  if (prizesQuery.isError || isNil(prizesQuery.data)) {
    return <Box>An error occurred fetching the prizes.</Box>
  }

  const toggleModalIsOpen = () => setIsModalOpen(!isModalOpen)

  const handleSubmitWrapper = handleSubmit((data) => {
    putLocationSweepstake.mutate({
      ...sweepstake,
      id: Number(drawingIdToEdit),
      licensedEstablishmentIds: sweepstake?.licensedEstablishments?.map(
        (x) => x.id
      ),
      sweepstakeData: {
        contestId: Number(drawingIdToEdit),
        id: sweepstake.sweepstakeData?.id ?? 0,
        state: prizes?.sweepstakeData?.state ?? 'Draft',
      },
      contestWinners: (data.Sweepstake.ContestWinner ?? []).map(
        (winner, index) => ({
          id: 0,
          groupId: String('00000000-0000-0000-0000-000000000000'), // blank guid denotes single mode sweepstakes
          contestId: Number(drawingIdToEdit),
          winnerNumber: Number(winner.winnerNumber ?? index + 1),
          cashPrize: winner.cashPrize ?? false,
          prizeType:
            winner.prizeType === 'Prize' ||
            winner.prizeType === 'Cash' ||
            winner.prizeType === 'Email'
              ? winner.prizeType
              : 'Prize',
          prizeAmount: Number(winner.prizeAmount ?? 0),
          prizeDescription: winner.prizeDescription ?? '',
        })
      ),
    })
  })

  return (
    <>
      <SectionHeader
        title="Prizes"
        showButton={true}
        onClickButton={() => {
          setDrawingIdToEdit(Number(prizes?.id))
          setDrawingNameToEdit(String(prizes?.name))
          setIsModalOpen(true)
          setValue(
            'Sweepstake.ContestWinner',
            Array.from({ length: prizes?.numLocationWinners ?? 1 }, (_, i) => {
              const winner = prizes?.contestWinners?.filter(
                (x) => x.contestId === prizes?.id
              )[i]
              return {
                winnerNumber: winner?.winnerNumber ?? i + 1,
                cashPrize: winner?.cashPrize ?? false,
                prizeType: winner?.prizeType ?? 'Prize',
                prizeAmount: Number(winner?.prizeAmount ?? 0),
                prizeDescription: winner?.prizeDescription ?? '',
              }
            })
          )
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          p: '16px',
          mb: '40px',
        }}
      >
        {prizes?.contestWinners?.length === 0 ? (
          <Typography>No prizes</Typography>
        ) : (
          ''
        )}

        <Grid container spacing={3}>
          {prizes?.contestWinners?.length !== 0 &&
            Array.from({ length: prizes?.numLocationWinners ?? 1 }, (_, i) => {
              const winner = prizes?.contestWinners?.filter(
                (x) => x.contestId === prizes?.id
              )[i]
              return (
                <Grid item xs={12} key={i}>
                  <Typography variant="body1">
                    <strong>Winner #{i + 1}: </strong>{' '}
                    {`${String(winner?.prizeDescription)}`}
                    {Number(winner?.prizeAmount) !== 0
                      ? ` - $${Number(winner?.prizeAmount ?? 0).toFixed(2)}`
                      : ''}
                  </Typography>
                </Grid>
              )
            })}
        </Grid>
      </Box>
      <Modal
        isOpen={isModalOpen}
        toggleIsOpen={() => {}}
        sx={{
          width: isMobile ? '100%' : '50%',
          height: isMobile ? '100%' : '55%',
        }}
      >
        <Box>
          <form onSubmit={handleSubmitWrapper}>
            <ModalFullHeader
              toggleIsOpen={toggleModalIsOpen}
              title={drawingNameToEdit ?? ''}
            />
            <ModalFullBody heightModal={isMobile ? '100%' : '55%'}>
              {Array.from({ length: numLocationWinners }).map((_, index) => {
                const contestWinners =
                  prizes?.contestWinners?.filter(
                    (x: { contestId: any }) => x.contestId === prizes?.id
                  ) ?? []
                const winner = contestWinners[index] ?? {}
                return (
                  <Grid
                    container
                    spacing={2}
                    marginTop={1}
                    borderTop={1}
                    key={index}
                  >
                    <Grid item xs={12} md={2}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 1 }}>
                        Winner #{index + 1}
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                        Prize Type
                      </InputLabel>
                      <Select
                        style={{ height: '40px' }}
                        placeholder="Prize"
                        {...register(
                          `Sweepstake.ContestWinner.${index}.prizeType`
                        )}
                        defaultValue={winner.prizeType ?? 'Prize'}
                      >
                        <MenuItem value="Prize">Prize</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Email">Email</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                        Prize Description
                      </InputLabel>
                      <TextField
                        fullWidth
                        {...register(
                          `Sweepstake.ContestWinner.${index}.prizeDescription`
                        )}
                        defaultValue={winner.prizeDescription ?? ''}
                        error={isPresent(
                          errors.Sweepstake?.ContestWinner?.[index]
                            ?.prizeDescription
                        )}
                        size="small"
                        inputProps={{ maxLength: 50 }}
                      />
                      {errors.Sweepstake?.ContestWinner?.[index]
                        ?.prizeDescription?.message && (
                        <FormFieldErrorMessage
                          message={
                            errors.Sweepstake?.ContestWinner?.[index]
                              ?.prizeDescription.message
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <InputLabel sx={{ fontWeight: 'normal', margin: 0 }}>
                        Prize Amount
                      </InputLabel>
                      <TextField
                        type="number"
                        {...register(
                          `Sweepstake.ContestWinner.${index}.prizeAmount`
                        )}
                        defaultValue={Number(winner.prizeAmount ?? 0)}
                        error={isPresent(
                          errors.Sweepstake?.ContestWinner?.[index]?.prizeAmount
                        )}
                        size="small"
                        inputProps={{ min: 0, max: 100000.0 }}
                        sx={{ width: '100%' }}
                      />
                      {errors.Sweepstake?.ContestWinner?.[index]?.prizeAmount
                        ?.message && (
                        <FormFieldErrorMessage
                          message={
                            errors.Sweepstake?.ContestWinner?.[index]
                              ?.prizeAmount.message
                          }
                        />
                      )}
                    </Grid>
                  </Grid>
                )
              })}
            </ModalFullBody>
            <ModalFullFooter>
              <Button onClick={toggleModalIsOpen} sx={{ marginRight: 1 }}>
                Cancel
              </Button>
              <ActivityButton
                type="submit"
                variant="contained"
                active={putLocationSweepstake.isPending}
              >
                Save
              </ActivityButton>
            </ModalFullFooter>
          </form>
        </Box>
      </Modal>
    </>
  )
}
